import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// 🎨 Dynamic & Smooth HSL Color Generator based on Unit Name
const generateColorFromString = (str) => {
    if (!str || str === 'N/A' || str === 'Unknown') return '#94a3b8'; // Fallback Slate color for N/A
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Balanced HSL values taake colors boht dark ya washed out na hon
    const hue = Math.abs(hash % 360);
    const saturation = 65 + (hash % 15); // 65% - 80%
    const lightness = 45 + (hash % 10);  // 45% - 55%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const UnitDistributionChart = ({ data = [], isLoading = false, totalRecords = 0 }) => {
    
    // Data ko clean aur format karna safe check ke sath
    const chartData = data.length > 0 ? data.map(item => ({
        name: item.name || 'Unknown',
        value: Number(item.value) || 0,
        color: generateColorFromString(item.name)
    })) : [];

    if (isLoading) {
        return (
            <div className="flex h-[350px] items-center justify-center rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                <p className="text-sm font-medium text-slate-400 dark:text-slate-500 animate-pulse">
                    Loading unit distribution...
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-950 w-full transition-all duration-300">
            {/* Header Section */}
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 font-medium">
                        Unit Distribution
                    </p>
                    <h2 className="mt-1 text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        Units
                    </h2>
                </div>
                <div className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
                    {totalRecords || chartData.length} Units Found
                </div>
            </div>

            {/* Divider */}
            <hr className="mt-4 border-slate-100 dark:border-slate-900" />

            {/* Chart Area */}
            <div className="mt-4 h-[280px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={65}
                            outerRadius={100}
                            paddingAngle={3}
                            stroke="transparent"
                        >
                            {chartData.map((entry, index) => (
                                <Cell 
                                    key={`cell-${index}-${entry.name}`} 
                                    fill={entry.color} 
                                    className="transition-all duration-300 hover:opacity-85 outline-none cursor-pointer"
                                />
                            ))}
                        </Pie>
                        
                        {/* Elegant Dark-themed Tooltip */}
                        <Tooltip 
                            contentStyle={{ 
                                borderRadius: '16px', 
                                border: 'none', 
                                backgroundColor: '#0f172a', 
                                color: '#fff',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                fontSize: '12px'
                            }} 
                            itemStyle={{ color: '#cbd5e1' }}
                        />

                        {/* Layout Configured Legend */}
                        <Legend 
                            layout="horizontal" 
                            verticalAlign="bottom" 
                            align="center"
                            iconType="circle"
                            iconSize={8}
                            wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                            formatter={(value, entry) => (
                                <span className="text-slate-600 dark:text-slate-400 font-medium px-1">
                                    {value}
                                </span>
                            )}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default UnitDistributionChart;